'use client';

import { LockIcon } from '@repo/icons';
import { useBreakpoint } from '@repo/react-hooks';
import { CopyButton } from '@repo/ui/copy-button';
import { SelectField } from '@repo/ui/select';
import { useTranslations } from 'next-intl';
import React, { memo, useEffect, useState } from 'react';
import QRCode from 'react-qr-code';
import { type ViewType } from '@/app/[locale]/(account)/account/deposit';
import DepositCryptoDetailsSkeleton from '@/components/common/skeletons/deposit-crypto-details-skeleton';
import { PAYMENT_TYPES } from '@/constants';
import { type FullMethod, usePaymentMethodsWithNetworks } from '@/hooks/usePaymentMethodsWithNetworks';
import { type NetworkOption } from '@/hooks/usePaymentMethodsWithNetworks/utils';

type DepositCryptoDetailsProps = {
  // view: ViewType;
  setView: (view: ViewType) => void;
  selectedMethod?: FullMethod;
  setSelectedMethod?: (method?: FullMethod) => void;
};

const DepositCryptoDetails = ({ setView, selectedMethod, setSelectedMethod }: DepositCryptoDetailsProps) => {
  const t = useTranslations('deposits');

  const { matches: isMobile } = useBreakpoint('sm', 'max');

  const { currencyCode, networkOptions } = selectedMethod ?? {};
  const { topMethods, otherMethods } = usePaymentMethodsWithNetworks({
    paymentType: PAYMENT_TYPES.DEPOSITS,
  });
  const [selectedNetwork, setSelectedNetwork] = useState<NetworkOption | null>(networkOptions?.[0] || null);

  const methodOptions = [...topMethods, ...otherMethods].map(({ currencyCode }) => ({
    label: currencyCode,
    value: currencyCode,
  }));

  useEffect(() => {
    if (!networkOptions?.[0]?.walletAddress) return;
    setSelectedNetwork(networkOptions[0]);
  }, [networkOptions]);

  if (!selectedNetwork) return <DepositCryptoDetailsSkeleton />;

  return (
    <div className="flex h-full w-full flex-col items-center justify-between space-y-4 self-center py-4 sm:max-w-[450px]">
      <div className="flex w-full items-center justify-between gap-2">
        <SelectField
          label={t('select-coins-label')}
          data={methodOptions}
          selectedValue={currencyCode!}
          className="sm:hidden"
          onChange={(value) => {
            const opt = [...topMethods, ...otherMethods].find((o) => o.currencyCode === value);
            if (opt) setSelectedMethod?.(opt);
            setView('CRYPTO_DETAILS');
          }}
        />
        <SelectField
          label={t('select-network-label')}
          data={networkOptions || []}
          selectedValue={selectedNetwork.value}
          className="w-full"
          onChange={(value) => {
            const opt = networkOptions?.find((o) => o.value === value);
            if (opt) setSelectedNetwork(opt);
          }}
        />
      </div>

      <div className="flex justify-center">
        <div className="bg-white p-1">
          <QRCode
            size={130}
            value={selectedNetwork.walletAddress}
          />
        </div>
      </div>

      <div className="flex w-full flex-col items-center justify-center space-y-1 px-2">
        <span className="w-full text-left vt-text-size-small">
          {t('crypto-address-label', { crypto: currencyCode || '' })}
        </span>
        <div className="flex w-full flex-wrap items-center space-x-2 rtl:space-x-reverse">
          <div className="flex flex-1 items-center justify-between space-x-2 rounded border p-2.5 vt-input-default-bg-normal vt-input-default-stroke-normal rtl:space-x-reverse">
            <span className="whitespace-normal break-all vt-font-highlight-font-family vt-utility-white">
              {selectedNetwork.walletAddress}
            </span>
            <LockIcon className="size-4" />
          </div>
          <CopyButton
            disabled={!selectedNetwork.walletAddress}
            className="size-9 rounded-full p-2 sm:size-auto"
            text={!isMobile ? t('crypto-address-copy-button-label') : ''}
            copiedText={!isMobile ? t('crypto-address-copied') : ''}
            copyContent={selectedNetwork.walletAddress}
          />
        </div>
      </div>

      <div className="flex w-full items-center justify-center">
        <span className="text-center font-medium vt-colors-statuses-warn-default vt-text-size-medium">
          {t('crypto-details-description-2', { crypto: currencyCode || '', network: selectedNetwork.label })}
        </span>
      </div>
    </div>
  );
};

export default memo(DepositCryptoDetails);
